import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase-config";
import { 
  collection, 
  getDocs, 
  query, 
  doc, 
  updateDoc, 
  deleteDoc, 
  addDoc, 
  where, 
  orderBy
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./AdminEscalationsPage.css";

function AdminEscalationsPage() {
  const navigate = useNavigate();
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyData, setModifyData] = useState({});
  const [sites, setSites] = useState([]);
  const [employeeNames, setEmployeeNames] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    loadEscalations();
    loadSites();
    loadEmployeeNames();
  }, []);

  const loadEscalations = async () => {
    try {
      setLoading(true);
      const escalationsRef = collection(db, 'adminEscalations');
      const q = query(escalationsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const escalationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("📊 Escalades chargées:", escalationsData.map(e => ({id: e.id, status: e.status, email: e.employeeEmail})));
      
      setEscalations(escalationsData);
    } catch (error) {
      console.error('Erreur lors du chargement des escalades:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const sitesRef = collection(db, 'sites');
      const snapshot = await getDocs(sitesRef);
      const sitesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSites(sitesData);
    } catch (error) {
      console.error('Erreur lors du chargement des sites:', error);
    }
  };

  const loadEmployeeNames = async () => {
    try {
      const employeeQuery = query(collection(db, 'employees'), where('email', '!=', null));
      const snapshot = await getDocs(employeeQuery);
      const employeeNamesData = {};
      snapshot.docs.forEach(doc => {
        const employeeData = doc.data();
        employeeNamesData[employeeData.email] = `${employeeData.prenom} ${employeeData.nom}`;
      });
      setEmployeeNames(employeeNamesData);
    } catch (error) {
      console.error('Erreur lors du chargement des noms des employés:', error);
    }
  };

  // Fonction pour obtenir le nom du site
  const getSiteName = (siteId) => {
    if (siteId === 'HORS_ZONE') return 'HORS ZONE';
    const site = sites.find(s => s.id === siteId);
    return site ? site.nom : siteId || 'Non disponible';
  };

  const calculateTotalWorkTime = (actions) => {
    const startAction = actions.find(a => a.type === 'START_WORK');
    const endAction = actions.find(a => a.type === 'END_WORK');
    
    if (!startAction || !endAction) return 0;
    
    const startTime = new Date(startAction.payload.startTime);
    const endTime = new Date(endAction.payload.endTime);
    const totalDuration = endTime.getTime() - startTime.getTime();
    
    // Soustraire le temps de pause
    const pauseTime = calculateTotalPauseTime(actions);
    
    const workTime = Math.max(0, totalDuration - pauseTime);
    
    console.log("⏱️ Calcul temps de travail:", {
      startTime: startTime.toLocaleString(),
      endTime: endTime.toLocaleString(),
      totalDuration: formatDuration(totalDuration),
      pauseTime: formatDuration(pauseTime),
      workTime: formatDuration(workTime)
    });
    
    return workTime;
  };

  const calculateTotalPauseTime = (actions) => {
    let totalPauseTime = 0;
    
    // Si les actions sont modifiées (START_PAUSE/END_PAUSE sans sequence), on les traite par paires dans l'ordre
    const pauses = [];
    for (let i = 0; i < actions.length; i++) {
      if (actions[i].type === 'START_PAUSE') {
        // Chercher le END_PAUSE suivant
        for (let j = i + 1; j < actions.length; j++) {
          if (actions[j].type === 'END_PAUSE') {
            const startTime = new Date(actions[i].payload.startTime);
            const endTime = new Date(actions[j].payload.endTime);
            const pauseDuration = endTime.getTime() - startTime.getTime();
            totalPauseTime += pauseDuration;
            pauses.push({start: startTime, end: endTime, duration: pauseDuration});
            i = j; // Passer à l'action suivante après END_PAUSE
            break;
          }
        }
      }
    }
    // Pour compatibilité, on garde aussi l'ancien système pour START_BREAK/END_BREAK avec sequence
    for (let i = 0; i < actions.length; i++) {
      if (actions[i].type === 'START_BREAK' && actions[i].sequence !== undefined) {
        const endBreak = actions.find(a => a.type === 'END_BREAK' && a.sequence > actions[i].sequence);
        if (endBreak) {
          const startTime = new Date(actions[i].payload.breakStartTime);
          const endTime = new Date(endBreak.payload.endTime);
          const pauseDuration = endTime.getTime() - startTime.getTime();
          totalPauseTime += pauseDuration;
          pauses.push({start: startTime, end: endTime, duration: pauseDuration});
        }
      }
    }
    // Log pour debug
    pauses.forEach(p => console.log("⏸️ Pause trouvée:", {start: p.start.toLocaleString(), end: p.end.toLocaleString(), duration: formatDuration(p.duration)}));
    console.log("⏸️ Temps de pause total:", formatDuration(totalPauseTime));
    return totalPauseTime;
  };

  const createBreaksFromEscalation = async (blockedActions, sessionId) => {
    for (let i = 0; i < blockedActions.length; i++) {
      if (blockedActions[i].type === 'START_BREAK') {
        const endBreak = blockedActions.find(a => 
          a.type === 'END_BREAK' && 
          a.sequence > blockedActions[i].sequence
        );
        
        if (endBreak) {
          const breakData = {
            sessionId: sessionId,
            startTime: new Date(blockedActions[i].payload.breakStartTime),
            endTime: new Date(endBreak.payload.endTime),
            startLocation: blockedActions[i].payload.position,
            endLocation: endBreak.payload.position,
            duration: new Date(endBreak.payload.endTime) - new Date(blockedActions[i].payload.breakStartTime),
            status: 'completed',
            adminCreated: true
          };
          
          await addDoc(collection(db, 'breaks'), breakData);
        }
      }
    }
  };

  const handleValidate = async (escalation) => {
    try {
      console.log("✅ Validation d'une escalade:", escalation.id);
      
      // Créer la session de travail
      const sessionData = {
        employeeEmail: escalation.employeeEmail,
        employeeName: employeeNames[escalation.employeeEmail] || escalation.employeeEmail,
        startTime: new Date(escalation.failedAction.payload.startTime),
        endTime: escalation.blockedActions.find(a => a.type === 'END_WORK')?.payload?.endTime 
          ? new Date(escalation.blockedActions.find(a => a.type === 'END_WORK').payload.endTime)
          : null,
        startLocation: escalation.failedAction.payload.position,
        endLocation: escalation.blockedActions.find(a => a.type === 'END_WORK')?.payload?.position || null,
        siteDeTravail: escalation.failedAction.payload.siteDeTravail,
        endSite: escalation.blockedActions.find(a => a.type === 'END_WORK')?.payload?.siteDeTravail || null,
        device: navigator.userAgent,
        status: 'completed',
        totalWorkTime: calculateTotalWorkTime(escalation.blockedActions),
        totalPauseTime: calculateTotalPauseTime(escalation.blockedActions),
        adminValidated: true,
        adminValidatedAt: new Date(),
        adminValidatedBy: auth.currentUser.email,
        originalEscalationId: escalation.id
      };
      
      // Ajouter à la collection workSessions
      const sessionRef = await addDoc(collection(db, 'workSessions'), sessionData);
      
      // Créer les pauses
      await createBreaksFromEscalation(escalation.blockedActions, sessionRef.id);
      
      // Marquer l'escalade comme validée
      const escalationRef = doc(db, 'adminEscalations', escalation.id);
      await updateDoc(escalationRef, {
        status: 'validated',
        validatedAt: new Date().toISOString(),
        validatedBy: auth.currentUser.email
      });
      
      await loadEscalations();
      alert('Escalade validée avec succès !');
      
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleReject = async (escalation) => {
    try {
      const escalationRef = doc(db, 'adminEscalations', escalation.id);
      await updateDoc(escalationRef, {
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
        rejectedBy: auth.currentUser.email
      });
      
      await loadEscalations();
      alert('Escalade refusée');
      
    } catch (error) {
      console.error('Erreur lors du refus:', error);
      alert('Erreur lors du refus');
    }
  };

  const handleModify = (escalation) => {
    console.log("🔧 Clic sur Modifier pour l'escalade:", escalation.id);
    console.log("📊 Données de l'escalade:", escalation);
    
    // Utiliser les données modifiées si elles existent, sinon les données originales
    const actionsToUse = escalation.modifiedActions || escalation.blockedActions;
    const startAction = actionsToUse.find(a => a.type === 'START_WORK');
    const endAction = actionsToUse.find(a => a.type === 'END_WORK');
    
    // Extraire date et heure de début
    const startDateTime = new Date(startAction.payload.startTime);
    const startDate = startDateTime.toISOString().split('T')[0];
    const startTime = startDateTime.toTimeString().split(' ')[0].substring(0, 5);
    
    // Extraire date et heure de fin
    let endDate = '';
    let endTime = '';
    if (endAction?.payload?.endTime) {
      const endDateTime = new Date(endAction.payload.endTime);
      endDate = endDateTime.toISOString().split('T')[0];
      endTime = endDateTime.toTimeString().split(' ')[0].substring(0, 5);
    }
    
    // Calculer le temps de pause
    const pauseTimeMinutes = Math.floor(calculateTotalPauseTime(actionsToUse) / 60000);
    
    setSelectedEscalation(escalation);
    setModifyData({
      startDate: startDate,
      startTime: startTime,
      endDate: endDate,
      endTime: endTime,
      pauseTime: pauseTimeMinutes,
      startSite: startAction.payload.siteDeTravail || '',
      endSite: endAction?.payload?.siteDeTravail || '',
      comment: escalation.modificationComment || ''
    });
    
    console.log("📝 Données de modification pré-remplies:", {
      startDate: startDate,
      startTime: startTime,
      endDate: endDate,
      endTime: endTime,
      pauseTime: pauseTimeMinutes,
      startSite: startAction.payload.siteDeTravail || '',
      endSite: endAction?.payload?.siteDeTravail || '',
      comment: escalation.modificationComment || ''
    });
    
    setShowModifyModal(true);
    console.log("✅ Modal ouvert");
  };

  const handleSaveModification = async () => {
    if (!selectedEscalation) return;
    
    try {
      console.log("💾 Sauvegarde de la modification...");
      console.log("📝 Données à sauvegarder:", modifyData);
      
      // Validation des dates et heures
      if (!modifyData.startDate || !modifyData.startTime || !modifyData.endDate || !modifyData.endTime) {
        alert('Veuillez remplir tous les champs de date et heure');
        return;
      }
      
      // Combiner date et heure de début
      const startDateTime = new Date(`${modifyData.startDate}T${modifyData.startTime}`);
      
      // Combiner date et heure de fin
      const endDateTime = new Date(`${modifyData.endDate}T${modifyData.endTime}`);
      
      // Vérifier que la fin est après le début
      if (endDateTime <= startDateTime) {
        alert('L\'heure de fin doit être après l\'heure de début');
        return;
      }
      
      // Vérifier que la durée ne dépasse pas 24h
      const durationMs = endDateTime.getTime() - startDateTime.getTime();
      const durationHours = durationMs / (1000 * 60 * 60);
      
      if (durationHours > 24) {
        alert('La durée de travail ne peut pas dépasser 24 heures');
        return;
      }
      
      // Vérifier que le temps de pause ne dépasse pas la durée totale
      const pauseTimeMs = modifyData.pauseTime * 60000; // convertir en millisecondes
      if (pauseTimeMs > durationMs) {
        alert('Le temps de pause ne peut pas dépasser la durée totale de travail');
        return;
      }
      
      // Créer les nouvelles actions modifiées
      const modifiedActions = [
        {
          type: 'START_WORK',
          payload: {
            startTime: startDateTime.toISOString(),
            siteDeTravail: modifyData.startSite
          }
        }
      ];
      
      // Ajouter les pauses si nécessaire
      if (modifyData.pauseTime > 0) {
        // Calculer les pauses (simplifié - on met tout au milieu)
        const totalDuration = endDateTime.getTime() - startDateTime.getTime();
        const pauseStart = new Date(startDateTime.getTime() + totalDuration / 2);
        
        modifiedActions.push({
          type: 'START_PAUSE',
          payload: {
            startTime: pauseStart.toISOString(),
            siteDeTravail: modifyData.startSite
          }
        });
        
        modifiedActions.push({
          type: 'END_PAUSE',
          payload: {
            endTime: new Date(pauseStart.getTime() + modifyData.pauseTime * 60000).toISOString(),
            siteDeTravail: modifyData.startSite
          }
        });
      }
      
      // Ajouter l'action de fin
      modifiedActions.push({
        type: 'END_WORK',
        payload: {
          endTime: endDateTime.toISOString(),
          siteDeTravail: modifyData.endSite
        }
      });
      
      // Mettre à jour l'escalade dans Firebase
      const escalationRef = doc(db, 'adminEscalations', selectedEscalation.id);
      await updateDoc(escalationRef, {
        status: 'modified',
        modifiedActions: modifiedActions,
        modificationComment: modifyData.comment,
        modifiedAt: new Date().toISOString(),
        modifiedBy: auth.currentUser?.uid || 'admin'
      });
      
      console.log("✅ Modification sauvegardée avec succès");
      
      // Fermer le modal et rafraîchir les données
      setShowModifyModal(false);
      setSelectedEscalation(null);
      setModifyData({});
      loadEscalations();
      
    } catch (error) {
      console.error("❌ Erreur lors de la sauvegarde:", error);
      alert('Erreur lors de la sauvegarde de la modification');
    }
  };

  const formatDuration = (milliseconds) => {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  const handleValidateModified = async (escalation) => {
    try {
      console.log("✅ Validation d'une escalade modifiée:", escalation.id);
      
      // Créer la session de travail avec les actions modifiées
      const sessionData = {
        employeeEmail: escalation.employeeEmail,
        employeeName: employeeNames[escalation.employeeEmail] || escalation.employeeEmail,
        startTime: new Date(escalation.modifiedActions.find(a => a.type === 'START_WORK').payload.startTime),
        endTime: new Date(escalation.modifiedActions.find(a => a.type === 'END_WORK').payload.endTime),
        startLocation: escalation.failedAction.payload.position,
        endLocation: escalation.blockedActions.find(a => a.type === 'END_WORK')?.payload?.position || null,
        siteDeTravail: escalation.modifiedActions.find(a => a.type === 'START_WORK').payload.siteDeTravail,
        endSite: escalation.modifiedActions.find(a => a.type === 'END_WORK').payload.siteDeTravail,
        device: navigator.userAgent,
        status: 'completed',
        totalWorkTime: calculateTotalWorkTime(escalation.modifiedActions),
        totalPauseTime: calculateTotalPauseTime(escalation.modifiedActions),
        adminModified: true,
        adminModifiedAt: new Date(),
        adminModifiedBy: auth.currentUser.email,
        originalEscalationId: escalation.id,
        adminComment: escalation.modificationComment || ''
      };
      
      // Ajouter à workSessions
      await addDoc(collection(db, 'workSessions'), sessionData);
      
      // Marquer l'escalade comme validée
      await updateDoc(doc(db, 'adminEscalations', escalation.id), {
        status: 'validated',
        validatedAt: new Date().toISOString(),
        validatedBy: auth.currentUser?.uid || 'admin'
      });
      
      console.log("✅ Escalade modifiée validée avec succès");
      loadEscalations();
      
    } catch (error) {
      console.error("❌ Erreur lors de la validation:", error);
      alert('Erreur lors de la validation');
    }
  };

  // Statuts possibles (pour l'affichage des filtres)
  const statusLabels = [
    { key: 'pending', label: 'En attente', statuses: ['pending', 'pending_admin_review'] },
    { key: 'modified', label: 'Modifiées', statuses: ['modified'] },
    { key: 'validated', label: 'Validées', statuses: ['validated'] },
    { key: 'rejected', label: 'Refusées', statuses: ['rejected'] },
    { key: 'all', label: 'Toutes', statuses: ['all'] }
  ];

  // Filtrage selon le statut sélectionné
  const filteredEscalations = selectedStatus === 'all'
    ? escalations
    : escalations.filter(e =>
        selectedStatus === 'pending'
          ? (e.status === 'pending' || e.status === 'pending_admin_review')
          : e.status === selectedStatus
      );

  // Pagination
  const totalPages = Math.ceil(filteredEscalations.length / ITEMS_PER_PAGE);
  const pagedEscalations = filteredEscalations.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  if (loading) {
    return <div className="loading">Chargement des escalades...</div>;
  }

  console.log("🎭 État du modal:", { showModifyModal, selectedEscalation: selectedEscalation?.id });

  return (
    <div className="admin-escalations-container">
      <h2>Gestion des escalades</h2>
      <div style={{marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
        {statusLabels.map(({key, label, statuses}) => (
          <span
            key={key}
            onClick={() => { setSelectedStatus(key); setPage(0); }}
            style={{
              padding: '5px 10px',
              backgroundColor:
                key === 'pending' ? '#007bff' :
                key === 'modified' ? '#ffc107' :
                key === 'validated' ? '#28a745' :
                key === 'rejected' ? '#dc3545' :
                '#6c757d',
              color: key === selectedStatus ? '#fff' : (key === 'modified' ? 'black' : 'white'),
              borderRadius: '4px',
              fontWeight: key === selectedStatus ? 'bold' : 'normal',
              cursor: 'pointer',
              border: key === selectedStatus ? '2px solid #222' : 'none',
              boxShadow: key === selectedStatus ? '0 0 0 2px #222' : 'none',
              textDecoration: key === selectedStatus ? 'underline' : 'none'
            }}
          >
            {label}: {key === 'all' ? escalations.length : escalations.filter(e => key === 'pending' ? (e.status === 'pending' || e.status === 'pending_admin_review') : statuses.includes(e.status)).length}
          </span>
        ))}
      </div>
      
      <div className="escalations-list">
        {pagedEscalations.length === 0 ? (
          <div className="no-escalations">
            Aucune escalade en attente
          </div>
        ) : (
          pagedEscalations.map(escalation => {
            console.log("🎭 Rendu escalade:", {id: escalation.id, status: escalation.status, email: escalation.employeeEmail});
            return (
            <div key={escalation.id} className={`escalation-card ${escalation.status}`}>
              <div className="escalation-header">
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <h3>{employeeNames[escalation.employeeEmail] || escalation.employeeEmail}</h3>
                  {(escalation.status === 'modified' || escalation.status === 'validated') && (
                    <span style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                      MANUEL
                    </span>
                  )}
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px'}}>
                  <span className="escalation-date">
                    <strong>Créée:</strong> {new Date(escalation.failedAction?.createdAt || escalation.createdAt).toLocaleString('fr-FR')}
                  </span>
                  <span className="escalation-date">
                    <strong>Transmise:</strong> {new Date(escalation.createdAt).toLocaleString('fr-FR')}
                  </span>
                  <span className={`status-badge ${escalation.status}`}>
                    {(escalation.status === 'pending' || escalation.status === 'pending_admin_review') && 'En attente'}
                    {escalation.status === 'validated' && 'Validée'}
                    {escalation.status === 'rejected' && 'Refusée'}
                    {escalation.status === 'modified' && 'Modifiée'}
                  </span>
                </div>
              </div>
              
              <div className="escalation-details">
                <p><strong>Problème:</strong> {escalation.escalationReason}</p>
                <p><strong>Actions bloquées:</strong> {escalation.blockedActions.length}</p>
                
                <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '4px'}}>
                  <h4 style={{margin: '0 0 10px 0', color: '#0066cc'}}>📍 Informations de localisation:</h4>
                  
                  <p><strong>Site de début:</strong> {getSiteName(escalation.failedAction?.payload?.siteDeTravail)}</p>
                  
                  {escalation.failedAction?.payload?.position && (
                    <p><strong>Position début:</strong> {escalation.failedAction.payload.position.lat?.toFixed(6)}, {escalation.failedAction.payload.position.lng?.toFixed(6)}</p>
                  )}
                </div>
                
                {escalation.blockedActions.find(a => a.type === 'END_WORK') && (
                  <>
                    <p><strong>Site de fin:</strong> {getSiteName(escalation.blockedActions.find(a => a.type === 'END_WORK')?.payload?.siteDeTravail)}</p>
                    
                    {escalation.blockedActions.find(a => a.type === 'END_WORK')?.payload?.position && (
                      <p><strong>Position fin:</strong> {escalation.blockedActions.find(a => a.type === 'END_WORK').payload.position.lat?.toFixed(6)}, {escalation.blockedActions.find(a => a.type === 'END_WORK').payload.position.lng?.toFixed(6)}</p>
                    )}
                  </>
                )}
                
                <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px'}}>
                  <h4 style={{margin: '0 0 10px 0', color: '#333'}}>⏱️ Temps de travail:</h4>
                  <p><strong>Temps de travail:</strong> {formatDuration(calculateTotalWorkTime(escalation.blockedActions))}</p>
                  <p><strong>Temps de pause:</strong> {formatDuration(calculateTotalPauseTime(escalation.blockedActions))}</p>
                </div>
                
                {/* Afficher les modifications si l'escalade a été modifiée */}
                {escalation.status === 'modified' && escalation.modifiedActions && (
                  <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
                    <h4 style={{margin: '0 0 10px 0', color: '#007bff'}}>📝 Modifications apportées:</h4>
                    <p><strong>Début:</strong> {new Date(escalation.modifiedActions.find(a => a.type === 'START_WORK').payload.startTime).toLocaleString('fr-FR')}</p>
                    <p><strong>Site début:</strong> {getSiteName(escalation.modifiedActions.find(a => a.type === 'START_WORK').payload.siteDeTravail)}</p>
                    
                    {escalation.modifiedActions.find(a => a.type === 'END_WORK') && (
                      <>
                        <p><strong>Fin:</strong> {new Date(escalation.modifiedActions.find(a => a.type === 'END_WORK').payload.endTime).toLocaleString('fr-FR')}</p>
                        <p><strong>Site fin:</strong> {getSiteName(escalation.modifiedActions.find(a => a.type === 'END_WORK').payload.siteDeTravail)}</p>
                      </>
                    )}
                    
                    <p><strong>Temps de pause:</strong> {formatDuration(calculateTotalPauseTime(escalation.modifiedActions))}</p>
                    <p><strong>Temps de travail:</strong> {formatDuration(calculateTotalWorkTime(escalation.modifiedActions))}</p>
                    
                    {escalation.modificationComment && (
                      <p><strong>Commentaire:</strong> {escalation.modificationComment}</p>
                    )}
                  </div>
                )}
                
                {/* Afficher les informations validées si l'escalade a été validée */}
                {escalation.status === 'validated' && (
                  <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '4px'}}>
                    <h4 style={{margin: '0 0 10px 0', color: '#155724'}}>✅ Session validée:</h4>
                    <p><strong>Validée par:</strong> {escalation.validatedBy || 'Admin'}</p>
                    <p><strong>Validée le:</strong> {escalation.validatedAt ? new Date(escalation.validatedAt).toLocaleString('fr-FR') : 'Date inconnue'}</p>
                    
                    {/* Afficher les données finales (modifiées ou originales) */}
                    {escalation.modifiedActions ? (
                      <>
                        <p><strong>Début:</strong> {new Date(escalation.modifiedActions.find(a => a.type === 'START_WORK').payload.startTime).toLocaleString('fr-FR')}</p>
                        <p><strong>Site début:</strong> {getSiteName(escalation.modifiedActions.find(a => a.type === 'START_WORK').payload.siteDeTravail)}</p>
                        
                        {escalation.modifiedActions.find(a => a.type === 'END_WORK') && (
                          <>
                            <p><strong>Fin:</strong> {new Date(escalation.modifiedActions.find(a => a.type === 'END_WORK').payload.endTime).toLocaleString('fr-FR')}</p>
                            <p><strong>Site fin:</strong> {getSiteName(escalation.modifiedActions.find(a => a.type === 'END_WORK').payload.siteDeTravail)}</p>
                          </>
                        )}
                        
                        <p><strong>Temps de pause:</strong> {formatDuration(calculateTotalPauseTime(escalation.modifiedActions))}</p>
                        <p><strong>Temps de travail:</strong> {formatDuration(calculateTotalWorkTime(escalation.modifiedActions))}</p>
                      </>
                    ) : (
                      <>
                        <p><strong>Début:</strong> {new Date(escalation.failedAction.payload.startTime).toLocaleString('fr-FR')}</p>
                        <p><strong>Site début:</strong> {getSiteName(escalation.failedAction.payload.siteDeTravail)}</p>
                        
                        {escalation.blockedActions.find(a => a.type === 'END_WORK') && (
                          <>
                            <p><strong>Fin:</strong> {new Date(escalation.blockedActions.find(a => a.type === 'END_WORK').payload.endTime).toLocaleString('fr-FR')}</p>
                            <p><strong>Site fin:</strong> {getSiteName(escalation.blockedActions.find(a => a.type === 'END_WORK').payload.siteDeTravail)}</p>
                          </>
                        )}
                        
                        <p><strong>Temps de pause:</strong> {formatDuration(calculateTotalPauseTime(escalation.blockedActions))}</p>
                        <p><strong>Temps de travail:</strong> {formatDuration(calculateTotalWorkTime(escalation.blockedActions))}</p>
                      </>
                    )}
                  </div>
                )}
              </div>
              
              <div className="escalation-actions">
                {(escalation.status === 'pending' || escalation.status === 'pending_admin_review') && (
                  <div style={{display: 'flex', gap: '5px'}}>
                    <button 
                      onClick={() => handleValidate(escalation)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Valider
                    </button>
                    <button 
                      onClick={() => handleReject(escalation)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Refuser
                    </button>
                    <button 
                      onClick={() => handleModify(escalation)}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Modifier
                    </button>
                  </div>
                )}
                
                {escalation.status === 'modified' && (
                  <div style={{display: 'flex', gap: '5px'}}>
                    <button 
                      onClick={() => handleValidateModified(escalation)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Valider
                    </button>
                    <button 
                      onClick={() => handleModify(escalation)}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Re-modifier
                    </button>
                  </div>
                )}
                
                {escalation.status === 'validated' && (
                  <span style={{color: '#28a745', fontWeight: 'bold'}}>✓ Validé</span>
                )}
                
                {escalation.status === 'rejected' && (
                  <span style={{color: '#dc3545', fontWeight: 'bold'}}>✗ Refusé</span>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>
      
      <button className="back-button" onClick={() => navigate(-1)}>
        ← Retour
      </button>

      {/* Modal de modification */}
      {showModifyModal && (
        <div className="modal-overlay" style={{ 
          border: '2px solid #2196f3',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="modal" style={{ 
            border: '2px solid #4caf50', 
            background: 'white',
            position: 'relative',
            display: 'block',
            visibility: 'visible',
            opacity: 1,
            zIndex: 10000,
            minWidth: '400px',
            minHeight: '300px'
          }}>
            <h3>Modifier la session</h3>
            <div className="modal-content">
              <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                <div className="form-group" style={{flex: 1}}>
                  <label>Date de début:</label>
                  <input 
                    type="date" 
                    value={modifyData.startDate || ''}
                    onChange={(e) => setModifyData({...modifyData, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group" style={{flex: 1}}>
                  <label>Heure de début:</label>
                  <input 
                    type="time" 
                    value={modifyData.startTime || ''}
                    onChange={(e) => setModifyData({...modifyData, startTime: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Site de début:</label>
                <select 
                  value={modifyData.startSite || ''}
                  onChange={(e) => setModifyData({...modifyData, startSite: e.target.value})}
                >
                  <option value="">Sélectionner un site</option>
                  <option value="HORS_ZONE">HORS ZONE</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>{site.nom}</option>
                  ))}
                </select>
              </div>
              
              <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                <div className="form-group" style={{flex: 1}}>
                  <label>Date de fin:</label>
                  <input 
                    type="date" 
                    value={modifyData.endDate || ''}
                    onChange={(e) => setModifyData({...modifyData, endDate: e.target.value})}
                  />
                </div>
                <div className="form-group" style={{flex: 1}}>
                  <label>Heure de fin:</label>
                  <input 
                    type="time" 
                    value={modifyData.endTime || ''}
                    onChange={(e) => setModifyData({...modifyData, endTime: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Site de fin:</label>
                <select 
                  value={modifyData.endSite || ''}
                  onChange={(e) => setModifyData({...modifyData, endSite: e.target.value})}
                >
                  <option value="">Sélectionner un site</option>
                  <option value="HORS_ZONE">HORS ZONE</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>{site.nom}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Temps total de pause (minutes):</label>
                <input 
                  type="number" 
                  value={modifyData.pauseTime}
                  onChange={(e) => setModifyData({...modifyData, pauseTime: parseInt(e.target.value) || 0})}
                  placeholder="Ex: 30 pour 30 minutes"
                />
                <small style={{color: '#666', fontSize: '12px'}}>
                  Temps total de toutes les pauses combinées
                </small>
              </div>
              
              <div className="form-group">
                <label>Commentaire (optionnel):</label>
                <textarea 
                  value={modifyData.comment || ''}
                  onChange={(e) => setModifyData({...modifyData, comment: e.target.value})}
                  placeholder="Raison de la modification..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-save" onClick={handleSaveModification}>
                Sauvegarder
              </button>
              <button className="btn-cancel" onClick={() => setShowModifyModal(false)}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc', background: page === 0 ? '#eee' : '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer'}}>←</button>
          <span style={{fontWeight: 'bold'}}>Page {page + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} style={{padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc', background: page === totalPages - 1 ? '#eee' : '#fff', cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer'}}>→</button>
        </div>
      )}
    </div>
  );
}

export default AdminEscalationsPage; 